import { useEffect, useRef } from 'react'

/**
 * Close a modal/sheet on Escape key (desktop) and Android back button.
 *
 * Android back: pushes a temporary history entry when the modal opens.
 * Pressing back pops it and triggers onClose instead of navigating away.
 *
 * @param {boolean} open  — whether the modal is currently visible
 * @param {Function} onClose — called to close the modal
 */
export function useModalClose(open, onClose) {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const closedByBackRef = useRef(false)

  useEffect(() => {
    if (!open) return

    closedByBackRef.current = false

    // --- Escape key ---
    function handleKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCloseRef.current()
      }
    }
    document.addEventListener('keydown', handleKey)

    // --- Android / browser back button ---
    const tag = 'modal-' + Date.now()
    window.history.pushState({ modal: tag }, '')

    function handlePop() {
      closedByBackRef.current = true
      onCloseRef.current()
    }
    window.addEventListener('popstate', handlePop)

    return () => {
      document.removeEventListener('keydown', handleKey)
      window.removeEventListener('popstate', handlePop)
      // Only clean up history if modal closed programmatically (not via back)
      if (!closedByBackRef.current && window.history.state?.modal === tag) {
        window.history.back()
      }
    }
  }, [open])
}
